import { useState } from 'react';
import { Search as SearchIcon, Clipboard, Image } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import EmptyState from '../components/EmptyState.jsx';
import { api } from '../services/api.js';
import { useToast } from '../contexts/ToastContext.jsx';
import { formatDate } from '../lib/utils.js';

export default function Search() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const submit = async (e) => {
    e.preventDefault();
    if (q.trim().length < 2) return toast.error('Type at least 2 characters');
    setBusy(true);
    try {
      const res = await api.search(q.trim());
      setResults(res.data);
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Search" subtitle="Find any clip, caption, or filename." />

      <form onSubmit={submit} className="card mb-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <SearchIcon size={14} className="absolute left-2 top-1/2 -tranzinc-y-1/2 text-zinc-500" />
          <input className="input pl-7" placeholder="Search..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <button className="btn-primary" disabled={busy}>{busy ? 'Searching...' : 'Search'}</button>
      </form>

      {results && (
        <div className="space-y-4">
          <section>
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase text-zinc-400">
              <Clipboard size={14} /> Clips ({results.clips.length})
            </h2>
            {results.clips.length === 0 ? <EmptyState title="No matching clips" /> : (
              <ul className="space-y-2">
                {results.clips.map((c) => (
                  <li key={c.id} className="card">
                    <pre className="whitespace-pre-wrap break-words text-sm text-zinc-100">{c.content}</pre>
                    <div className="mt-1 text-xs text-zinc-500">{formatDate(c.created_at)}</div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase text-zinc-400">
              <Image size={14} /> Snaps ({results.snaps.length})
            </h2>
            {results.snaps.length === 0 ? <EmptyState title="No matching snaps" /> : (
              <ul className="space-y-2">
                {results.snaps.map((s) => (
                  <li key={s.id} className="card">
                    <div className="text-sm font-medium">{s.filename}</div>
                    {s.caption && <div className="text-sm text-zinc-300">{s.caption}</div>}
                    <div className="mt-1 text-xs text-zinc-500">{formatDate(s.created_at)}</div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
